import {
  ColumnApi,
  GetRowIdFunc,
  GridApi,
  GridReadyEvent,
  SelectionChangedEvent,
  IRowNode,
} from '@ag-grid-community/core';
import { PermissionEnum } from '@creditsights/api-ui-web';
import { Loader, Pagination, AGTable, agTableGetColumns } from '@creditsights/styleguide-web';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import classNames from 'classnames/bind';
import useFetchUserPermissions from '../../../../hooks/permissions/useFetchUserPermissions';
import useTableOverlayHandler from '../../../../hooks/table/useTableOverlayHandler';
import { TableQueryResult } from '../../../../hooks/table/useTableQueryParams';
import { IssuerListItemDto } from '../../../../services/dtos/issuer/issuerList.dto';
import { hasPermissionToExport } from '../../../../utils/permissionsUtils';
import { additionalDefaultColDef } from '../../../Companies/Company.constants';
import NoDataComponent from './NoDataComponent';
import { NoDataComponentProps } from './NoDataComponent/NoDataComponent';
import {
  WATCHLIST_TABLE_COL_PERSISTENCE_KEY,
  WATCHLIST_TABLE_COL_DEFS,
  WATCHLIST_TABLE_PAGE_SIZE,
} from './WatchlistTable.constants';
import styles from './WatchlistTable.module.scss';

const cx = classNames.bind(styles);

const Table = AGTable<IssuerListItemDto>;

interface WatchlistTableProps extends TableQueryResult {
  id: string;
  issuers: IssuerListItemDto[];
  isIssuersLoading: boolean;
  onIssuerSelection: (tagIds: string[]) => void;
  noDataComponentParams: NoDataComponentProps;
  totalResults: number;
  gridApiRef: React.MutableRefObject<GridApi | null>;
}

const WatchlistTable = ({
  id,
  issuers,
  isIssuersLoading,
  onIssuerSelection,
  noDataComponentParams,
  page,
  onPageChanged,
  onSortChanged,
  totalResults,
  sort,
  gridApiRef,
}: WatchlistTableProps): React.ReactElement | null => {
  const { data: userPermissions } = useFetchUserPermissions();
  const columnApiRef = useRef<ColumnApi | null>(null);

  const columnDefs = useMemo(
    () =>
      agTableGetColumns(WATCHLIST_TABLE_COL_PERSISTENCE_KEY, WATCHLIST_TABLE_COL_DEFS).filter(
        (col) =>
          userPermissions?.permissions.includes(PermissionEnum.RESEARCH) ||
          col.colId !== 'latestRecommendation.weighting'
      ),
    [userPermissions]
  );

  const onGridReady = useCallback((event: GridReadyEvent) => {
    // eslint-disable-next-line no-param-reassign
    gridApiRef.current = event.api;
    columnApiRef.current = event.columnApi;
    event.columnApi.applyColumnState({ state: [{ colId: sort.sortField, sort: sort.sortOrderAsc ? 'asc' : 'desc' }] });
    event.api.sizeColumnsToFit();
  }, []);

  const onSelectionChanged = useCallback(
    ({ api }: SelectionChangedEvent<IssuerListItemDto>) => {
      const nodes = api
        .getSelectedNodes()
        .map((node) => node.data?.tagId)
        .filter(Boolean) as string[];

      onIssuerSelection(nodes);
    },
    [onIssuerSelection]
  );

  const noRowsOverlayComponent = useMemo(() => (issuers ? NoDataComponent : null), [!!issuers]);
  const getRowId: GetRowIdFunc<IssuerListItemDto> = useCallback((params) => params.data.tagId, []);

  const { suppressNoRowsOverlay } = useTableOverlayHandler(gridApiRef, {
    isLoading: isIssuersLoading || !issuers,
    data: issuers,
  });

  useEffect(() => {
    gridApiRef.current?.ensureIndexVisible(0, 'top');
    window.scrollTo(0, 0);
  }, [page]);

  useEffect(() => {
    gridApiRef.current?.deselectAll();
    onIssuerSelection([]);

    if (columnApiRef.current) {
      onSortChanged({ columnApi: columnApiRef.current });
    }
  }, [id]);

  const isExternalFilterPresent = useCallback(() => hasPermissionToExport(userPermissions), [userPermissions]);
  const doesExternalFilterPass = useCallback((node: IRowNode<IssuerListItemDto>) => !node.data?.hidden, []);

  return (
    <div className={cx('body', { 'no-issuers': issuers.length === 0 })}>
      <div className={cx('table')}>
        <Table
          additionalDefaultColDef={additionalDefaultColDef}
          columnDefs={columnDefs}
          columnPersistenceKey={WATCHLIST_TABLE_COL_PERSISTENCE_KEY}
          getRowId={getRowId}
          loadingOverlayComponent={Loader}
          noRowsOverlayComponent={noRowsOverlayComponent}
          noRowsOverlayComponentParams={noDataComponentParams}
          onGridReady={onGridReady}
          onSelectionChanged={onSelectionChanged}
          onSortChanged={onSortChanged}
          rowData={issuers || []}
          rowSelection="multiple"
          suppressNoRowsOverlay={suppressNoRowsOverlay}
          suppressRowClickSelection
          suppressRowVirtualisation
          domLayout="autoHeight"
          isExternalFilterPresent={isExternalFilterPresent}
          doesExternalFilterPass={doesExternalFilterPass}
        />
      </div>
      {!!issuers?.length && totalResults > WATCHLIST_TABLE_PAGE_SIZE && (
        <div className={cx('pagination')}>
          <Pagination
            activePage={page}
            onPageChange={onPageChanged}
            totalPages={Math.ceil((totalResults || 0) / WATCHLIST_TABLE_PAGE_SIZE) || 1}
          />
        </div>
      )}
    </div>
  );
};

export default WatchlistTable;
